import { useMemo, useCallback, useState } from 'react';

/**
 * Custom hook for performance optimizations in category components
 */
export const usePerformanceOptimizations = () => {
  
  // Debounced search function
  const createDebouncedSearch = useCallback((callback, delay = 300) => {
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => callback(...args), delay);
    };
  }, []);

  // Service filtering utilities
  const filterServices = useCallback((services, searchTerm, category, filters = {}) => {
    if (!services) return [];
    
    return services.filter(service => {
      // Search term filter
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const matchesSearch = 
          service.title?.toLowerCase().includes(term) ||
          service.description?.toLowerCase().includes(term) ||
          service.tags?.some(tag => tag.toLowerCase().includes(term));
        
        if (!matchesSearch) return false;
      }

      // Category filter
      if (category && category !== 'all') {
        if (service.category !== category) return false;
      }

      // Price range filter
      if (filters.priceRange) {
        const { min, max } = filters.priceRange;
        if (service.price < min || service.price > max) return false;
      }

      // Rating filter
      if (filters.minRating) {
        if (service.rating < filters.minRating) return false;
      }

      // Duration filter
      if (filters.duration) {
        if (service.duration !== filters.duration) return false;
      }

      // Features filter
      if (filters.features && filters.features.length > 0) {
        const hasRequiredFeatures = filters.features.every(feature =>
          service.features?.includes(feature)
        );
        if (!hasRequiredFeatures) return false;
      }

      return true;
    });
  }, []);

  // Service sorting utilities
  const sortServices = useCallback((services, sortBy = 'relevance', sortOrder = 'asc') => {
    if (!services) return [];
    
    const sorted = [...services].sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'price':
          comparison = a.price - b.price;
          break;
        case 'rating':
          comparison = b.rating - a.rating; // Higher rating first by default
          break;
        case 'name':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'duration':
          comparison = a.duration.localeCompare(b.duration);
          break;
        case 'popularity':
          comparison = b.reviewCount - a.reviewCount; // More reviews first
          break;
        case 'newest':
          comparison = new Date(b.createdAt) - new Date(a.createdAt);
          break;
        default: // relevance
          // Combine rating and review count for relevance
          const aRelevance = a.rating * Math.log(a.reviewCount + 1);
          const bRelevance = b.rating * Math.log(b.reviewCount + 1);
          comparison = bRelevance - aRelevance;
      }
      
      return sortOrder === 'desc' ? -comparison : comparison;
    });
    
    return sorted;
  }, []);

  // Chunk array for batch processing
  const chunkArray = useCallback((array, chunkSize = 50) => {
    if (!array) return [];
    
    const chunks = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  }, []);

  // Virtual scrolling utilities
  const getVirtualScrollConfig = useCallback((itemHeight = 200, containerHeight = 600, overscan = 5) => {
    return {
      getVisibleRange: (scrollTop, itemCount) => {
        const start = Math.floor(scrollTop / itemHeight);
        const visibleCount = Math.ceil(containerHeight / itemHeight);
        const end = Math.min(start + visibleCount + overscan, itemCount);
        const startWithOverscan = Math.max(0, start - overscan);
        
        return { start: startWithOverscan, end };
      },
      getTotalHeight: (itemCount) => itemCount * itemHeight,
      getItemTop: (index) => index * itemHeight
    };
  }, []);

  return {
    createDebouncedSearch,
    filterServices,
    sortServices,
    chunkArray,
    getVirtualScrollConfig
  };
};

/**
 * Custom hook for managing favorites
 */
export const useFavorites = (initialFavorites = []) => {
  const [favorites, setFavorites] = useState(() => new Set(initialFavorites));

  const toggleFavorite = useCallback((serviceId) => {
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      if (newFavorites.has(serviceId)) {
        newFavorites.delete(serviceId);
      } else {
        newFavorites.add(serviceId);
      }
      return newFavorites;
    });
  }, []);

  const addFavorite = useCallback((serviceId) => {
    setFavorites(prev => new Set([...prev, serviceId]));
  }, []);

  const removeFavorite = useCallback((serviceId) => {
    setFavorites(prev => {
      const newFavorites = new Set(prev);
      newFavorites.delete(serviceId);
      return newFavorites;
    });
  }, []);

  const isFavorite = useCallback((serviceId) => {
    return favorites.has(serviceId);
  }, [favorites]);

  const favoriteCount = useMemo(() => favorites.size, [favorites]);

  return {
    favorites,
    toggleFavorite,
    addFavorite,
    removeFavorite,
    isFavorite,
    favoriteCount
  };
};

/**
 * Custom hook for filtered and sorted services
 */
export const useFilteredServices = (services, searchTerm, category, filters, sortBy, sortOrder) => {
  const { filterServices, sortServices } = usePerformanceOptimizations();

  const filteredAndSorted = useMemo(() => {
    const filtered = filterServices(services, searchTerm, category, filters);
    return sortServices(filtered, sortBy, sortOrder);
  }, [services, searchTerm, category, filters, sortBy, sortOrder, filterServices, sortServices]);

  return filteredAndSorted;
};

const performanceHooks = {
  usePerformanceOptimizations,
  useFavorites,
  useFilteredServices
};

export default performanceHooks;
